import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../../../components/ui/breadcrumb';
import { Filter, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { ENV } from "../../../utils/env.js";
import { useToaster } from "../../../contexts/ToasterContext.js";

// Shimmer effect components for SlackChannelFindingsPage
const TableShimmer = ({ rows = 8 }) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#C0ACF3]">
            <th className="text-left p-3 text-[#000000] font-medium">Status</th>
            <th className="text-left p-3 text-[#000000] font-medium">Alert ID</th>
            <th className="text-left p-3 text-[#000000] font-medium">User</th>
            <th className="text-left p-3 text-[#000000] font-medium">Source</th>
            <th className="text-left p-3 text-[#000000] font-medium">Message Preview</th>
            <th className="text-left p-3 text-[#000000] font-medium">Detection Time</th>
            <th className="text-left p-3 text-[#000000] font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, index) => (
            <tr key={index} className="border-b border-[#F4F0FD]">
              <td className="p-3">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-20 h-6 bg-gray-200 rounded-full animate-pulse"></div>
                </div>
              </td>
              <td className="p-3">
                <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
              </td>
              <td className="p-3">
                <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
              </td>
              <td className="p-3">
                <div className="w-20 h-6 bg-gray-200 rounded animate-pulse"></div>
              </td>
              <td className="p-3">
                <div className="flex flex-col space-y-1">
                  <div className="w-32 h-3 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-16 h-3 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </td>
              <td className="p-3">
                <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
              </td>
              <td className="p-3">
                <div className="flex space-x-2">
                  <div className="w-12 h-6 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-12 h-6 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SlackChannelFindingsPage = () => {
  const { channelId } = useParams();
  const { showToast } = useToaster();
  
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [channelName, setChannelName] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [revealed, setRevealed] = useState(() => {
    // Load revealed state from localStorage on component mount
    const saved = localStorage.getItem('slack-revealed-global');
    return saved ? JSON.parse(saved) : {};
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    has_next: false,
    has_previous: false,
    last_page: 1
  });
  const [resolvingAlerts, setResolvingAlerts] = useState(new Set());
  // Multi-select and bulk action state
  const [selected, setSelected] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [bulkResolveLoading, setBulkResolveLoading] = useState(false);

  const fetchChannelFindings = useCallback(async (channelId, page = 1, per_page = 10) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await axios.get(
        `${ENV.REACT_APP_URL}api/slack/alerts/${channelId}?page=${page}&per_page=${per_page}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      )
      
      console.log('Channel findings response:', response.data)
      
      // Handle paginated response - check for both 'alerts' and 'data' keys
      const alertsData = response.data.alerts || response.data.data || response.data
      const alertsArray = Array.isArray(alertsData) ? alertsData : []
      
      console.log('Found alerts data:', alertsArray)
      setFindings(alertsArray)
      
      // Set pagination based on response structure
      setPagination({
        page: response.data.page || page,
        per_page: response.data.per_page || per_page,
        total: response.data.total_count || response.data.total || alertsArray.length,
        has_next: (response.data.page || page) < (response.data.total_pages || response.data.last_page || 1),
        has_previous: (response.data.page || page) > 1,
        last_page: response.data.total_pages || response.data.last_page || 1
      })
    } catch (error) {
      console.error('Error fetching channel findings:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch channel findings'
      setError(errorMessage)
      showToast(errorMessage, 'error')
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    if (channelId) {
      fetchChannelFindings(channelId);
      // Fetch channel name for header/breadcrumb
      (async () => {
        try {
          const response = await axios.get(
            `${ENV.REACT_APP_URL}api/slack/channels`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("jwt")}`,
              },
            }
          );
          const channels = response?.data?.channels || [];
          const match = channels.find(ch => (ch.id || ch.channel_id) === channelId);
          setChannelName(match?.name || match?.channel_name || channelId);
        } catch (e) {
          setChannelName(channelId);
        }
      })();
    }
  }, [channelId, fetchChannelFindings]);

  // Filter findings based on filterType - handle both capitalized and lowercase fields
  const filteredFindings = findings.filter(finding => {
    if (filterType === 'all') return true
    const isVerified = finding.Verified || finding.verified
    const isResolved = finding.Resolved || finding.resolved
    
    if (filterType === 'verified') return isVerified
    if (filterType === 'unverified') return !isVerified
    if (filterType === 'resolved') return isResolved
    if (filterType === 'unresolved') return !isResolved
    return true
  })

  // Selection helpers
  const getAlertId = (alert, index) => (alert.ID || alert.id || index);
  const visibleIds = (filteredFindings || []).map((f, i) => getAlertId(f, i));
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected[id]);
  const selectedCount = Object.entries(selected).filter(([id, v]) => v && visibleIds.includes(id)).length;
  const selectedAlerts = () => (filteredFindings || []).filter((f, i) => selected[getAlertId(f, i)]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      if (prev[id] && !next[id]) {
        setRowErrors((errs) => {
          const n = { ...errs };
          delete n[id];
          return n;
        });
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected((prev) => {
        const next = { ...prev };
        visibleIds.forEach((id) => { delete next[id]; });
        return next;
      });
      clearRowErrors(visibleIds);
    } else {
      setSelected((prev) => {
        const next = { ...prev };
        visibleIds.forEach((id) => { next[id] = true; });
        return next;
      });
    }
  };

  const clearRowErrors = (rowIds = null) => {
    if (rowIds === null) {
      setRowErrors({});
    } else {
      setRowErrors((prev) => {
        const next = { ...prev };
        rowIds.forEach((id) => delete next[id]);
        return next;
      });
    }
  };

  // Bulk resolve alerts
  const handleBulkResolveAlerts = async () => {
    const items = selectedAlerts();
    if (items.length === 0) return;
    setBulkResolveLoading(true);
    clearRowErrors(items.map((a, i) => getAlertId(a, i)));

    const errors = {};
    let successCount = 0;

    for (let i = 0; i < items.length; i++) {
      const alert = items[i];
      const alertId = alert.ID || alert.id;
      try {
        const response = await axios.post(
          `${ENV.REACT_APP_URL}api/slack/alerts/${alertId}/resolve`,
          {},
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("jwt")}`,
            },
          }
        );

        if (response.data?.message?.includes('resolved successfully')) {
          successCount++;
          setFindings((prev) => prev.map((f) => ((f.ID || f.id) === alertId ? { ...f, Resolved: true, resolved: true } : f)));
        } else if (response.data?.message === 'Alert is already resolved') {
          successCount++;
          setFindings((prev) => prev.map((f) => ((f.ID || f.id) === alertId ? { ...f, Resolved: true, resolved: true } : f)));
        } else {
          errors[alertId] = response.data?.message || 'Failed to resolve';
        }
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Failed to resolve';
        errors[alertId] = msg;
      }
    }

    setRowErrors(errors);
    setBulkResolveLoading(false);

    if (successCount > 0) {
      const idsToClear = items.map((a, i) => getAlertId(a, i));
      setSelected((prev) => {
        const next = { ...prev };
        idsToClear.forEach((id) => { delete next[id]; });
        return next;
      });
      showToast(`Successfully resolved ${successCount} alert(s)`, 'success');
    }
    const failCount = Object.keys(errors).length;
    if (failCount > 0) {
      showToast(`${failCount} alert(s) failed. Check individual rows for details.`, 'error');
    }
  };

  // Pagination controls
  const handlePrevPage = () => {
    if (pagination.has_previous && pagination.page > 1) {
      const newPage = pagination.page - 1
      setPagination(prev => ({ ...prev, page: newPage }))
      fetchChannelFindings(channelId, newPage, pagination.per_page)
    }
  }

  const handleNextPage = () => {
    if (pagination.has_next && pagination.page < pagination.last_page) {
      const newPage = pagination.page + 1
      setPagination(prev => ({ ...prev, page: newPage }))
      fetchChannelFindings(channelId, newPage, pagination.per_page)
    }
  }

  const handlePerPageChange = (perPage) => {
    setPagination(prev => ({ ...prev, per_page: perPage, page: 1 }))
    fetchChannelFindings(channelId, 1, perPage)
  }


  if (loading) {
    return (
      <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
        <div className="mx-auto">
          <div className="mb-8 space-y-2">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/identity-management">Identity Management</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/integration/slack">Slack</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{channelName || 'Channel'}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <div className="flex items-center gap-3">
              <h1 className="text-black text-2xl md:text-3xl font-bold">Slack • {channelName || channelId}</h1>
            </div>
          </div>
          <div className="bg-white border border-[#C0ACF3] rounded-2xl shadow-xl overflow-hidden relative">
            <div className="p-6 border-b border-[#C0ACF3]">
              <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-64"></div>
            </div>
            <div className="p-6">
              <TableShimmer rows={8} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
        <div className="mx-auto">
          <div className="mb-8 space-y-2">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/identity-management">Identity Management</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/integration/slack">Slack</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{channelName || 'Channel'}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="text-center py-16">
            <p className="text-black text-lg mb-4">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
      <div className="mx-auto">
        <div className="mb-8 space-y-2">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/identity-management">Identity Management</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/integration/slack">Slack</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{channelName || channelId}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <h1 className="text-black text-2xl md:text-3xl font-bold">Slack {channelName || channelId}</h1>
          </div>
        </div>

        <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
            {/* Filter Section */}
            <div className="py-3 border-b border-gray-200 sticky top-0 z-10 bg-white px-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-[#000000]" />
                  <span className="text-[#000000] text-sm">Filter by:</span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setFilterType('all')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      filterType === 'all'
                        ? 'bg-[#441a9a] text-white border border-[#441a9a] hover:bg-[#3a1080]'
                        : 'bg-white text-[#441a9a] border border-[#441a9a] hover:bg-[#f3effd]'
                    }`}
                  >
                    All ({findings.length})
                  </button>
                  <button
                    onClick={() => setFilterType('verified')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      filterType === 'verified'
                        ? 'bg-[#441a9a] text-white border border-[#441a9a] hover:bg-[#3a1080]'
                        : 'bg-white text-[#441a9a] border border-[#441a9a] hover:bg-[#f3effd]'
                    }`}
                  >
                    Active ({findings.filter(f => f.Verified || f.verified).length})
                  </button>
                  <button
                    onClick={() => setFilterType('unverified')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      filterType === 'unverified'
                        ? 'bg-[#441a9a] text-white border border-[#441a9a] hover:bg-[#3a1080]'
                        : 'bg-white text-[#441a9a] border border-[#441a9a] hover:bg-[#f3effd]'
                    }`}
                  >
                    Unvalidated ({findings.filter(f => !(f.Verified || f.verified)).length})
                  </button>
                  <button
                    onClick={() => setFilterType('resolved')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      filterType === 'resolved'
                        ? 'bg-[#441a9a] text-white border border-[#441a9a] hover:bg-[#3a1080]'
                        : 'bg-white text-[#441a9a] border border-[#441a9a] hover:bg-[#f3effd]'
                    }`}
                  >
                    Resolved ({findings.filter(f => f.Resolved || f.resolved).length})
                  </button>
                  <button
                    onClick={() => setFilterType('unresolved')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      filterType === 'unresolved'
                        ? 'bg-[#441a9a] text-white border border-[#441a9a] hover:bg-[#3a1080]'
                        : 'bg-white text-[#441a9a] border border-[#441a9a] hover:bg-[#f3effd]'
                    }`}
                  >
                    Unresolved ({findings.filter(f => !(f.Resolved || f.resolved)).length})
                  </button>
                </div>
                </div>
                {selectedCount > 0 && (
                  <div className="flex items-center gap-2 rounded border border-[#C0ACF3] bg-[#F4F0FD] px-2 py-1">
                    <span className="text-xs text-[#000000]">Selected {selectedCount}</span>
                    <button
                      onClick={handleBulkResolveAlerts}
                      disabled={bulkResolveLoading}
                      className={`px-3 py-1 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1`}
                    >
                      {bulkResolveLoading && (
                        <div className="animate-spin rounded-full h-3 w-3 border border-gray-400 border-t-transparent"></div>
                      )}
                      Resolve Alerts
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6">
              {!loading && filteredFindings.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-black text-lg mb-4">
                    {filterType === 'all' 
                      ? 'No findings found for this channel'
                      : `No ${filterType==='verified'? "active" : "unvalidated"} findings found for this channel`
                    }
                  </p>
                  <p className="text-black text-sm">
                    {filterType === 'all' 
                      ? 'This channel appears to be clean of any detected secrets.'
                      : `Try changing the filter to see other findings.`
                    }
                  </p>
                </div>
              ) : (
                <div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left p-3 text-[#000000] font-medium w-10">
                            <input
                              type="checkbox"
                              aria-label="Select all"
                              className="h-4 w-4 rounded border-[#C0ACF3] text-[#6B5FB3] focus:ring-[#6B5FB3] align-middle"
                              checked={allSelected}
                              onChange={toggleSelectAll}
                            />
                          </th>
                          <th className="text-left p-3 text-[#000000] font-medium">Status</th>
                          <th className="text-left p-3 text-[#000000] font-medium">Resolved</th>
                          <th className="text-left p-3 text-[#000000] font-medium">Alert ID</th>
                          <th className="text-left p-3 text-[#000000] font-medium">User</th>
                          <th className="text-left p-3 text-[#000000] font-medium">Source</th>
                          <th className="text-left p-3 text-[#000000] font-medium">Raw Result</th>
                          <th className="text-left p-3 text-[#000000] font-medium">Detection Time</th>
                          <th className="text-left p-3 text-[#000000] font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFindings.map((alert, index) => {
                          const isVerified = alert.Verified || alert.verified
                          const isResolved = alert.Resolved || alert.resolved
                          const alertId = alert.ID || alert.id
                          const username = alert.Username || alert.username
                          const source = alert.Source || alert.source
                          const secret = alert.Secret || alert.secret
                          const timestamp = alert.Timestamp || alert.timestamp
                          const rowId = getAlertId(alert, index)
                          const isSelected = !!selected[rowId]
                          const hasError = !!rowErrors[rowId]
                          
                          const handleResolveToggle = async () => {
                            if (resolvingAlerts.has(alertId)) return; // Prevent multiple clicks
                            
                            setResolvingAlerts(prev => new Set([...prev, alertId]));
                            
                            try {
                              const response = await axios.post(
                                `${ENV.REACT_APP_URL}api/slack/alerts/${alertId}/resolve`,
                                {},
                                {
                                  headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${localStorage.getItem("jwt")}`,
                                  },
                                }
                              )
                              
                          console.log('Resolve response:', response.data)
                          
                          // Handle successful resolution
                          if (response.data.message && response.data.message.includes('resolved successfully')) {
                            // Update the local state to reflect the resolved status
                            setFindings(prevFindings => 
                              prevFindings.map(finding => 
                                (finding.ID || finding.id) === alertId 
                                  ? { ...finding, Resolved: true, resolved: true }
                                  : finding
                              )
                            )
                            
                            // Show success message
                            showToast(response.data.message, 'success')
                          } else if (response.data.message === 'Alert is already resolved') {
                            showToast('This alert is already resolved', 'info')
                          }
                            } catch (error) {
                              console.error('Error toggling resolved status:', error)
                              const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update resolved status'
                              showToast(errorMessage, 'error')
                            } finally {
                              setResolvingAlerts(prev => {
                                const newSet = new Set(prev);
                                newSet.delete(alertId);
                                return newSet;
                              });
                            }
                          }
                          
                          return (
                            <React.Fragment key={alertId || index}>
                            <tr className={`border-b border-gray-100 hover:bg-gray-50 ${isSelected ? 'bg-[#E9E3FB]' : ''} ${hasError ? 'bg-red-50 border-red-200' : ''}`}>
                              <td className="p-3 align-middle text-center">
                                <input
                                  type="checkbox"
                                  aria-label={`Select alert ${rowId}`}
                                  className="h-4 w-4 rounded border-[#C0ACF3] text-[#6B5FB3] focus:ring-[#6B5FB3] align-middle"
                                  checked={isSelected}
                                  onChange={() => toggleSelect(rowId)}
                                />
                              </td>
                              <td className="p-3">
                                <div className="flex items-center space-x-2">
                                  <div className={`w-3 h-3 rounded-full ${
                                    isVerified ? 'bg-green-500' : 'bg-gray-400'
                                  }`}></div>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    isVerified ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {isVerified ? 'Active' : 'Unvalidated'}
                                  </span>
                                </div>
                              </td>
                              <td className="p-3">
                                <div className="flex items-center space-x-2">
                                  <div className={`w-3 h-3 rounded-full ${
                                    isResolved ? 'bg-blue-500' : 'bg-gray-400'
                                  }`}></div>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    isResolved ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {isResolved ? 'Resolved' : 'Unresolved'}
                                  </span>
                                </div>
                              </td>
                              <td className="p-3">
                                <span className="font-mono text-xs text-gray-600">
                                  {alertId ? alertId.toString().slice(0, 8) : 'N/A'}
                                </span>
                              </td>
                              <td className="p-3">
                                <div className="flex items-center space-x-2">
                                  <div className="w-6 h-6 bg-[#C0ACF3] rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                  </div>
                                  <span className="text-black font-medium">{username || 'Unknown User'}</span>
                                </div>
                              </td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  source === 'AWS_ACCESS_KEY' ? 'bg-red-100 text-red-700' :
                                  source === 'API_KEY' ? 'bg-yellow-100 text-yellow-700' :
                                  source === 'PASSWORD' ? 'bg-orange-100 text-orange-700' :
                                  source === 'Slack' ? 'bg-purple-100 text-purple-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {source}
                                </span>
                              </td>
                              <td className="p-3 text-sm text-gray-900 break-words max-w-xs" style={{wordBreak: 'break-all'}}>
                                <div className="flex items-center">
                                  <span className="font-mono">
                                    {secret
                                      ? revealed[`raw_result-${channelId}-${index}`]
                                        ? secret
                                        : `${secret.slice(0, 6)}...`
                                      : 'N/A'}
                                  </span>
                                  {secret && (
                                    <button
                                      className="ml-2 text-gray-400 hover:text-gray-700 focus:outline-none"
                                      title={revealed[`raw_result-${channelId}-${index}`] ? 'Hide' : 'Show'}
                                      onClick={() => {
                                        const key = `raw_result-${channelId}-${index}`;
                                        const newRevealed = { ...revealed, [key]: !revealed[key] };
                                        setRevealed(newRevealed);
                                        localStorage.setItem('slack-revealed-global', JSON.stringify(newRevealed));
                                      }}
                                    >
                                      {revealed[`raw_result-${channelId}-${index}`] ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="p-3">
                                <span className="text-gray-600 text-xs">
                                  {timestamp ? new Date(parseFloat(timestamp) * 1000).toLocaleString() : 'Unknown'}
                                </span>
                              </td>
                              <td className="p-3">
                                <div className="flex items-center space-x-2">
                                  {!isResolved && (
                                    <button
                                      className={`px-2 py-1 text-xs rounded transition-colors ${
                                        resolvingAlerts.has(alertId) || selectedCount > 0
                                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                          : 'bg-green-200 text-green-700 hover:bg-green-300'
                                      }`}
                                      onClick={handleResolveToggle}
                                      disabled={resolvingAlerts.has(alertId) || selectedCount > 0}
                                    >
                                      {resolvingAlerts.has(alertId) ? 'Resolving...' : 'Resolve'}
                                    </button>
                                  )}
                                  {isResolved && (
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                                      Resolved
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {hasError && (
                              <tr className="border-b border-red-200 bg-red-50">
                                <td colSpan={9} className="p-3">
                                  <div className="flex items-center gap-2 text-red-700">
                                    <svg
                                      className="w-4 h-4 text-red-600 flex-shrink-0"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                      />
                                    </svg>
                                    <span className="text-sm font-medium">Error:</span>
                                    <span className="text-sm">{rowErrors[rowId]}</span>
                                    <button
                                      onClick={() => clearRowErrors([rowId])}
                                      className="ml-auto text-red-500 hover:text-red-700 focus:outline-none"
                                      aria-label="Dismiss error"
                                    >
                                      <EyeOff className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Bulk actions bar moved to sticky filter bar for consistency */}

                  {/* Pagination Controls */}
                  {pagination.total > 0 && (
                    <div className="mt-6 flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-600">
                          Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} results
                        </span>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">Per page:</span>
                          <select
                            value={pagination.per_page}
                            onChange={(e) => handlePerPageChange(Number(e.target.value))}
                            className="border border-gray-300 rounded px-2 py-1 text-sm"
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handlePrevPage}
                          disabled={!pagination.has_previous}
                          className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                            pagination.has_previous
                              ? 'bg-[#C0ACF3] text-black hover:bg-[#a088ea]'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          Previous
                        </button>
                        
                        <span className="text-sm text-gray-600">
                          Page {pagination.page} of {pagination.last_page}
                        </span>
                        
                        <button
                          onClick={handleNextPage}
                          disabled={!pagination.has_next}
                          className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                            pagination.has_next
                              ? 'bg-[#C0ACF3] text-black hover:bg-[#a088ea]'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
        </div>
      </div>
    </div>
  );
};

export default SlackChannelFindingsPage;
